import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { HTTP_RES_ERROR_CODE } from '@fitness-force/enum';
import { catchError, Observable, throwError, switchMap, filter, take, timeout } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { AuthSevices } from './auth.service';

@Injectable()
export class IntercepterService implements HttpInterceptor{
  publicApis: Array<string>;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private refreshAttemptCount = 0;
  private maxRefreshAttempts = 3;
  private lastRefreshTime = 0;
  private minRefreshInterval = 1000; // Minimum 1 second between refresh attempts

  constructor(
    private router: Router,
    private injector: Injector
    ) {
    this.publicApis= CONST_URL_SEGEMENTS.PUBLIC_APIS;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authService = this.injector.get(AuthSevices)
    if (req.headers.get("skip")){
      return next.handle(req);
    }
    if (this.validInterceptor(req.url)) {
      let tokenizedReq : any;
      if(req.method.toLowerCase() == 'patch')
      {
        tokenizedReq = req.clone({
          setHeaders:
          {
            'Content-Type':  'application/json-patch+json',
            'authorization': `Bearer ${authService.tokenFromStorage ? authService.tokenFromStorage : ''}`,
          }
        });
      }
      else{
        tokenizedReq = req.clone({
          setHeaders:
          {
            'Content-Type':  'application/json',
            'authorization': `Bearer ${authService.tokenFromStorage ? authService.tokenFromStorage : ''}`,
          }
        });
      }
     

      return next.handle(tokenizedReq)
      .pipe((catchError((err:any) => {        
        return this.handleAuthError(err, req, next);
      })))
    }
    return next.handle(req)
    .pipe((catchError((err:any) => {      
      return this.handleAuthError(err, req, next);
    })))
  }

  private validInterceptor(requestUrl: string): boolean {
    const positionIndicator = 'fftesting.fitnessforce.com/';
    const positionIndicatorOfAssets = 'dist/'

    const position = (requestUrl.indexOf(positionIndicatorOfAssets) == -1 ) ? requestUrl.indexOf(positionIndicator) : requestUrl.indexOf(positionIndicatorOfAssets) ;
    const positionIndicatorLength = (requestUrl.indexOf(positionIndicatorOfAssets) == -1 ) ? positionIndicator.length  : positionIndicatorOfAssets.length
    if (position > 0) {

      const destination: string = requestUrl.substring(position + positionIndicatorLength);
      for (const address of this.publicApis) {
        if (new RegExp(address).test(destination)) {
          return false;
        }
      }
    }
    return true;
  }


  handleAuthError(err: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler):Observable<any> {
    const authService = this.injector.get(AuthSevices);
    
    // Handle 401 and 428 status codes with token refresh
    if (err.status === HTTP_RES_ERROR_CODE.UNAUTHORIZED || err.status === 428) {
      console.log('🔑 Token expired, attempting refresh...', request.url);
      
      // Check if we've exceeded max refresh attempts
      if (this.refreshAttemptCount >= this.maxRefreshAttempts) {
        console.log('🚫 Max refresh attempts exceeded, redirecting to logout');
        this.resetRefreshState();
        this.router.navigateByUrl(`/auth/logout`);
        return throwError(() => new Error('Max refresh attempts exceeded'));
      }
      
      if (!this.isRefreshing) {
        // Check if we're trying to refresh too frequently (debouncing)
        const now = Date.now();
        const timeSinceLastRefresh = now - this.lastRefreshTime;
        
        if (timeSinceLastRefresh < this.minRefreshInterval && this.lastRefreshTime > 0) {
          console.log(`⏸️ Debouncing refresh attempt (${timeSinceLastRefresh}ms since last attempt)`);
          // Wait for the current refresh to complete instead of starting a new one
          return this.refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            timeout(30000),
            switchMap(token => {
              if (token === 'REFRESH_FAILED') {
                this.resetRefreshState();
                this.router.navigateByUrl(`/auth/logout`);
                return throwError(() => new Error('Token refresh failed'));
              }
              return this.retryRequestWithNewToken(request, next, token);
            }),
            catchError((error: any) => {
              if (error.name === 'TimeoutError') {
                this.resetRefreshState();
                this.router.navigateByUrl(`/auth/logout`);
                return throwError(() => new Error('Token refresh timeout'));
              }
              return throwError(() => error);
            })
          );
        }
        
        // Set the flag IMMEDIATELY to prevent race conditions
        this.isRefreshing = true;
        this.refreshAttemptCount++;
        this.lastRefreshTime = now;
        this.refreshTokenSubject.next(null);
        
        console.log(`🔄 Starting refresh attempt ${this.refreshAttemptCount}/${this.maxRefreshAttempts}`);
        
        // Validate refresh token exists before attempting refresh
        const refreshToken = authService.refreshTokenFromStorage;
        if (!refreshToken) {
          console.log('🚫 No refresh token available, redirecting to logout');
          this.resetRefreshState();
          this.router.navigateByUrl(`/auth/logout`);
          return throwError(() => new Error('No refresh token available'));
        }
        
        return authService.refreshToken().pipe(
          switchMap((response: any) => {
            console.log('✅ Token refresh successful, retrying original request');
            this.isRefreshing = false;
            this.refreshAttemptCount = 0; // Reset on success
            this.refreshTokenSubject.next(response.token);
            
            // Reset the subject after allowing pending requests to complete
            setTimeout(() => {
              this.refreshTokenSubject.next(null);
            }, 200);
            
            // Retry the original request with new token
            return this.retryRequestWithNewToken(request, next, response.token);
          }),
          catchError((refreshError: any) => {
            console.log('❌ Token refresh failed:', refreshError);
            this.isRefreshing = false;
            this.refreshTokenSubject.next('REFRESH_FAILED');
            
            // Reset the subject after a delay
            setTimeout(() => {
              this.refreshTokenSubject.next(null);
            }, 200);
            
            // Check specific error conditions for logout
            const shouldLogout = 
              refreshError.message === 'No refresh token available' || 
              refreshError.status === 401 || 
              refreshError.status === 403 ||
              refreshError.status === 400 || // Bad request - invalid refresh token
              this.refreshAttemptCount >= this.maxRefreshAttempts;
              
            if (shouldLogout) {
              console.log('🚪 Redirecting to logout due to refresh failure');
              this.resetRefreshState();
              this.router.navigateByUrl(`/auth/logout`);
            }
            
            return throwError(() => refreshError);
          })
        );
      } else {
        console.log('⏳ Token refresh already in progress, waiting...');
        // If already refreshing, wait for the refresh to complete
        return this.refreshTokenSubject.pipe(
          filter(token => token !== null), // Wait for a valid token or error
          take(1), // Take only the first emission
          timeout(30000), // 30 second timeout to prevent hanging
          switchMap(token => {
            if (token === 'REFRESH_FAILED') {
              console.log('🚪 Redirecting to logout due to refresh failure (waiting case)');
              this.resetRefreshState();
              this.router.navigateByUrl(`/auth/logout`);
              return throwError(() => new Error('Token refresh failed'));
            }
            console.log('✅ Using refreshed token for waiting request');
            // Retry the original request with the new token
            return this.retryRequestWithNewToken(request, next, token);
          }),
          catchError((error: any) => {
            // Handle timeout or other errors
            if (error.name === 'TimeoutError') {
              console.log('⏰ Token refresh timeout, redirecting to logout');
              this.resetRefreshState();
              this.router.navigateByUrl(`/auth/logout`);
              return throwError(() => new Error('Token refresh timeout'));
            }
            return throwError(() => error);
          })
        );
      }
    }
    
    if (err.status === HTTP_RES_ERROR_CODE.FORBIDDEN) {
      return throwError(() => err);
    }
    return throwError(() => err);
  }

  private retryRequestWithNewToken(request: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {
    // Clone the original request with the new token
    let retriedReq: HttpRequest<any>;
    if(request.method.toLowerCase() == 'patch') {
      retriedReq = request.clone({
        setHeaders: {
          'Content-Type': 'application/json-patch+json',
          'authorization': `Bearer ${token}`
        }
      });
    } else {
      retriedReq = request.clone({
        setHeaders: {
          'Content-Type': 'application/json',
          'authorization': `Bearer ${token}`
        }
      });
    }
    
    return next.handle(retriedReq);
  }

  private resetRefreshState(): void {
    this.isRefreshing = false;
    this.refreshAttemptCount = 0;
    this.lastRefreshTime = 0;
    this.refreshTokenSubject.next(null);
  }


}
